import { Button } from '@/components/atoms'
import { UploadFile, SectionTitle } from '@/components/molecules'
import UploadOutcomeDisplay from '@/components/molecules/UploadFile/UploadOutcomeDisplay'
import { Constants, Size, Variant } from '@/utils/constants/commonConstants'
import { useDispatch, useSelector } from 'react-redux'
import { setUploadOutcome, resetUploadOutcome } from '@/store/slices/salesTargetSlice'
import { useState } from 'react'
import { FONT_SIZE } from '@/utils/constants/globalStyle'

/**
 * FileUploadWithoutModal component renders a file upload interface with optional download template and view table buttons.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.headerTitle=''] - The title to display in the header.
 * @param {Function} props.handleDownloadTemplate - Function to handle the download template button click.
 * @param {string} [props.viewTableButtonText=Constants.ViewDataTable] - Text for the view table button.
 * @param {Function} props.handleViewTable - Function to handle the view table button click.
 * @param {string} [props.label=''] - Label for the file upload input.
 * @param {string} [props.description=''] - Description for the file upload input.
 * @param {Array} props.acceptedExtensions - Array of accepted file extensions.
 * @param {Array} props.files - Array of files to be uploaded.
 * @param {Function} props.onUpload - Function to handle file upload.
 * @param {string} props.feedbackType - Type of feedback to display.
 * @param {Function} [props.onUploadDelete=() => {}] - Function to handle file deletion.
 * @param {string} [props.helperText=''] - Helper text to display.
 * @param {Function} [props.onUploadError=() => {}] - Function to handle upload errors.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {string} [props.containerClassName=''] - Additional class names for the container.
 * @param {string} props.variant - Variant of the upload input.
 * @param {string} props.type - Type of the upload input.
 * @param {boolean} [props.showDownloadTemplate=true] - Flag to show or hide the download template button.
 * @param {boolean} [props.showHandleViewTable=true] - Flag to show or hide the view table button.
 * @param {boolean} [props.disabled=false] - Flag to disable the upload input.
 * @param {boolean} [props.isCsvValidationRequired=false] - Flag to indicate if CSV product ids validation is required.
 * @param {boolean} [props.isValidateProductIds=false] - Flag to indicate if product ids validation is required.
 *
 * @returns {JSX.Element} The rendered FileUploadWithoutModal component.
 */

export default function FileUploadWithoutModal({
  headerTitle = '',
  handleDownloadTemplate,
  label = '',
  description = '',
  acceptedExtensions,
  files,
  onUpload,
  feedbackType,
  onUploadDelete = () => { },
  helperText = '',
  onUploadError = () => { },
  className = '',
  containerClassName = '',
  variant,
  type,
  showDownloadTemplate = true,
  disabled = false,
  isCsvValidationRequired = false,
  required = true,
  isValidateProductIds = false,
  productIdsFilename,
  onValidatedFileName,
  activeTab = 'parent',
  customRules = null,
  handleValidateProductIds = false,
  handleUploadDelete = false,
  uploadResult = false
} = {}) {
  const dispatch = useDispatch();
  const defaultUploadOutcome = { totalCount: 0, nonExistentIds: [] };
  const uploadOutcome = useSelector(
    state => state.salesTarget.tabWiseData?.[activeTab]?.uploadOutcome || defaultUploadOutcome
  );
  const [uploading, setUploading] = useState(false);
  if (!handleValidateProductIds)
    handleValidateProductIds = response => {
      dispatch(setUploadOutcome({
        activeTab,
        totalCount: response.totalProductIds,
        nonExistentIds: response.data
      }));
      if (onValidatedFileName && response.fileName) {
        onValidatedFileName(response.fileName);
      }
    };

  if (!handleUploadDelete)
    handleUploadDelete = () => {
      onUploadDelete();
      dispatch(resetUploadOutcome({ activeTab }));
    };
  const getVariant = () => {
    const val = isValidateProductIds && uploadOutcome.totalCount > 0 ? Constants.Complete : variant
    return val;
  }
  const uploadOutcomeDisplayComponent = () => {
    if (activeTab === 'productsPointMultiplier') {
      return (isValidateProductIds && uploadResult.totalCount > 0 &&
        <UploadOutcomeDisplay
          totalCount={uploadResult.totalCount}
          nonExistentIds={uploadResult.nonExistentIds}
          fileName={uploadResult.fileName}
          onDelete={isValidateProductIds ? handleUploadDelete : undefined}
        />
      );
    }
    return (isValidateProductIds && uploadOutcome.totalCount > 0 && (
      <UploadOutcomeDisplay
        totalCount={uploadOutcome.totalCount}
        nonExistentIds={uploadOutcome.nonExistentIds}
        fileName={productIdsFilename}
        onDelete={isValidateProductIds ? handleUploadDelete : undefined}
      />
    ))
  }
  const displayHeader =
    (showDownloadTemplate && !isValidateProductIds) || (isValidateProductIds && uploadOutcome.totalCount === 0);

  return (
    <>
      {displayHeader && (
        <div className="mb-6 flex items-center justify-between">
          <SectionTitle fontSize={FONT_SIZE.XXXL} label={headerTitle}>{headerTitle}</SectionTitle>
          <div className="w-full flex justify-end">
            <Button
              handleButton={handleDownloadTemplate}
              uiSchema={{
                title: Constants.DownloadTemplate,
                size: Size.Medium,
                variant: Variant.Secondary
              }}
            />
          </div>
        </div>
      )}
      <UploadFile
        isDark={true}
        acceptedExtensions={acceptedExtensions}
        description={description}
        label={label}
        variant={getVariant()}
        required={required}
        files={files}
        onUpload={onUpload}
        feedbackType={feedbackType}
        onUploadDelete={handleUploadDelete}
        helperText={helperText}
        onUploadError={onUploadError}
        type={type}
        className={className}
        containerClassName={containerClassName}
        dataTestId="upload-file"
        disabled={disabled}
        isCsvValidationRequired={isCsvValidationRequired}
        isValidateProductIds={isValidateProductIds}
        uploading={uploading}
        setUploading={setUploading}
        customRules={customRules}
        validateProductIdsCallback={handleValidateProductIds}
      />
      {isValidateProductIds && uploadOutcomeDisplayComponent()}
    </>
  )
}
